import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect, MultiSelectChangeParams } from 'primereact/multiselect';
import useDashboardStore from 'store/dashboard-store';
import useDatasetIdentifiersStore from 'store/dataset-identifiers-store';
import useScadaStore from 'store/scada-store';
import useSelectedScadaTagsStore from 'store/selected-scada-tags-store';
import CalendarWrapper from '../../basic/calendar-wrapper/CalendarWrapper';
import DatasetLineChart from '../charts/dataset-line-chart/DatasetLineChart';
import './Scada.scss';
import ScadaTags from './components/ScadaTags';
import { ScadaProps, ScadaTagsProps } from './scadaTypes';
import { defaultDateTime, defaultElementParameters } from './utils/default-values';
import { getScadaImageUrl, showCords, tempId } from './utils/scadaUtils';

export default function Scada(props: ScadaProps) {
	const [parent, setParent] = useState(defaultElementParameters);
	const [dialog, setDialog] = useState(false);
	const [dateTime, setDateTime] = useState(defaultDateTime);
	const [layers, setLayers] = useState<string[]>([]);
	const [gridSize, setGridSize] = useState(0.5);
	const [labelPosition, setLabelPosition] = useState({x: 0, y: 0});
	const isEditMode = useDashboardStore(state => state.isEditMode);
	
	const ref = useRef<HTMLDivElement>(null);
	const scadaStore = useScadaStore();
	const selectedIdsStore = useDatasetIdentifiersStore();
	const selectedTagsStore = useSelectedScadaTagsStore();

	const selectedIds = selectedIdsStore.ids;
	const selectedTags = selectedTagsStore.tags;

	const scada = scadaStore.scadaMap.get(props.id ?? tempId(props));
	let timeout: NodeJS.Timeout | null = null;
	const layerOptions = scada?.labelLayers ?? [];

	useEffect(() => {
		if (!scada) {
			scadaStore.addScada({
				scadaId: props.id ?? tempId(props),
				labels: props.elements ?? [],
				labelLayers: props.labelLayers ?? [],
			});
		} else {
			scadaStore.updateScadaLabels(props.id!, props.elements ?? []);
		}
	}, []);

	useEffect(() => {
		const rect = ref.current!.getBoundingClientRect();
		setParent({
			width: rect.width,
			height: rect.height,
			top: rect.top,
			left: rect.left,
		});
	}, [ref.current]);

	useEffect(() => {
		setLayers(layerOptions.filter(g => g.visible).map(g => g.layer));
	}, [layerOptions])

	useEffect(() => {
		if(selectedTags.length > 0 && selectedTags[0].dataset.x && selectedTags[0].dataset.y) {
			setLabelPosition({
        x: Number(selectedTags[0]?.dataset.x),
        y: Number(selectedTags[0]?.dataset.y),
      });
		}
	}, [selectedTags])

	const handleScroll = () => {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(() => {
			const rect = ref.current!.getBoundingClientRect();
			setParent({
				width: rect.width,
				height: rect.height,
				top: rect.top,
				left: rect.left,
			});
		}, 200);
	};

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const onContextMenu = (e: React.MouseEvent) => {
		handleContextMenu(e, openDialog);
	};

	const openDialog = () => {
		setDialog(true);
	};

	const handleLayerMultiselect = (e: MultiSelectChangeParams) => {
		const updLayers = layerOptions.map(l => ({
			...l,
			visible: e.value.includes(l.layer),
		}));
		scadaStore.updateScadaLabelLayers(props.id!, updLayers);
		setLayers(e.value);
	};

	const handleCoordChange = (x: number, y: number) => {
		if (selectedTags.length === 1) {
			setLabelPosition({ x, y });
		}
	};

	const elemProps: ScadaTagsProps = {
		...props,
		date: dateTime.manualTime ? dateTime.date : undefined,
		returnDate: e => setDateTime({ ...dateTime, date: e }),
		openDialogCallBack: openDialog,
		parent,
		layerOptions,
		gridSize,
		onCoordChange: handleCoordChange,
	};

	const handleContextMenu = (e: React.MouseEvent, cb: () => void) => {
    if (!isEditMode && selectedTags.length !== 0) {
        openScadaContextMenu(e, () => {
            cb();
        });
    }
    e.preventDefault();
	};

	const cancelSelections = (e: React.MouseEvent) => {
    if (e.ctrlKey) return;

    if (new Date().getTime() - scadaStore.lastClick < 500) return;

		selectedTags.forEach(el => el.children[0].classList.remove("selectedLabel"));
    selectedTagsStore.clear();
		selectedIdsStore.clear();
	};

	return (
		<>
			<div className='flex flex-column gap-3'>
				<div className='flex justify-content-between'>
					{props.showScadaSettings && (
						<div className='flex gap-1'>
							<CalendarWrapper
								value={dateTime.date}
								onChange={e => setDateTime({ date: e, manualTime: true })}
							/>
							{dateTime.manualTime && (
								<Button
									icon='pi pi-sync'
									disabled={!dateTime.manualTime}
									onClick={() => setDateTime(defaultDateTime)}
								/>
							)}
						</div>
					)}
					<div className='flex gap-5 justify-content-end w-full'>
						{isEditMode && (
							<div className='flex gap-5 align-items-baseline'>
								{selectedTags.length === 1 && selectedTags[0]?.dataset.scadaId === props.id && (
									<span className='border-1 border-gray-300 border-round scada-p-9px'>
										{`x: ${labelPosition.x.toFixed(1)}%, y: ${labelPosition.y.toFixed(1)}%`}
									</span>
								)}
								<div className='flex gap-2 align-items-center'>
									<label htmlFor='gridSize'>Grid, %:</label>
									<InputNumber
										inputId='gridSize'
										value={gridSize}
										onChange={e => setGridSize(e.value ?? 1)}
										inputStyle={{ width: '3rem' }}
										maxFractionDigits={1}
									/>
								</div>
							</div>
						)}

						{layerOptions.length > 1 && (
							<MultiSelect
								value={layers}
								onChange={handleLayerMultiselect}
								options={layerOptions.map(g => g.layer)}
								className='w-8rem'
								maxSelectedLabels={0}
								selectedItemsLabel='Layers'
								placeholder='Layers'
							/>
						)}
					</div>
				</div>
				<div
					ref={ref}
					className='scadaViewContainer relativePos'
					onClick={cancelSelections}
					onMouseMove={e => showCords(e, isEditMode)}
					onContextMenu={onContextMenu}>
					<img
						alt='SCADA schematics'
						className='scadaView'
						src={getScadaImageUrl(props.scadaBackgroundId)}
					/>

					<ScadaTags {...elemProps} />
				</div>
			</div>

			<Dialog
				header='SCADA tags chart'
				visible={dialog}
				style={{ width: 'min(100vw, 1000px)', height: '500px' }}
				modal={false}
				resizable
				draggable
				maximizable
				onHide={() => setDialog(false)}>
				<DatasetLineChart
					identifiers={[...selectedIds]}
					referenceTime={
						dateTime.manualTime ? dateTime.date.toISOString() : undefined
					}
					showDateSelection
					dynamicHeight
					chartDialogOpen={dialog}
				/>
			</Dialog>
		</>
	);
}
